/**
 * Back and Forward put a scrolled list back where it was (Packet CX-4, fix
 * rounds 1 and 2).
 *
 * WHY THE APP HAS TO DO THIS NOW. Opening a page resets the window to the top
 * (routeScroll.ts). That is right going forward and wrong for the trip home:
 * pressing Back asks the browser to restore the list's saved offset, but the
 * browser restores it onto whatever document is on screen at that instant. A
 * class page with one post, or a page still loading, is short, so the offset is
 * clamped to that page's maximum, and Chrome keeps retrying the restore only
 * while the window still sits AT that maximum. After the reset the window sits
 * at 0, so it never retries, and scroll anchoring then rides the list down to
 * its footer. Measured by the refute round on the real app: My classes
 * scrolled to 2219, open a class with one post, read it for 2.5s, Back: the
 * list came back at about 4939, the bottom, 4 times out of 4. Before the reset
 * it came back at 2219.
 *
 * WHAT THIS DOES. It remembers where each history entry was scrolled when it
 * was LEFT, and on a Back or Forward to a different page it puts that offset
 * back as soon as the document is tall enough to hold it:
 *
 *   - It re-applies the offset ONCE, never twice, and gives up after 15 seconds
 *     (a page that never grows that tall is left wherever the browser put it).
 *     Fifteen, not four: the refute round measured a list that took longer than
 *     four seconds to render on a slow phone, and a give-up that fires before
 *     the rows arrive lands the reader at the bottom, worse than hub-staging.
 *     The reader's own input is the real safeguard, below.
 *   - It yields to the reader at once, but only to input that MOVES THE PAGE. A
 *     wheel turn or a scroll key counts when the window really moved in that
 *     direction within the next two frames; a touch drag counts when the window
 *     moved; a press on the scrollbar counts at once. A tap, a wheel turn
 *     downward or a PageDown on a list that is still too short to scroll moves
 *     nothing, so it is not the reader taking over, and the restore carries on
 *     (the re-check measured the earlier version, which counted every such
 *     event, ending at the bottom 6 of 6 where hub-staging restores). While a
 *     wheel or key is still being judged the restore waits a frame rather than
 *     scrolling over it.
 *   - Input is counted from the moment the restore STARTS (the new page
 *     mounting), not from the Back press: for the 220ms the old page fades out,
 *     a tap or a key lands on the page being LEFT and says nothing about the
 *     page being returned to. The earlier version, which counted from the Back
 *     press, landed the list at its bottom 10 times out of 10 after a wheel
 *     turn, PageDown, Space, ArrowDown or a touch right after Back. Input after
 *     the page has mounted still ends it, frame by frame.
 *   - It only runs for a POP that changes the PATHNAME. The menu drawer, the
 *     Assistant panel and the photo viewers all push and pop history entries on
 *     the SAME pathname; those are never touched.
 *   - What is remembered is only ever the offset of a page that had MOUNTED and
 *     was not itself still waiting for a restore. A forward navigation (PUSH)
 *     reads the window at the moment of leaving. A Back or Forward out of an
 *     entry reads the last offset a scroll event reported for that page: scroll
 *     events are dispatched at the next rendering opportunity, after the
 *     popstate, so that value is the page's own whether or not the browser has
 *     already moved the window to the entry being returned to (it does that at
 *     different moments in different browsers). A page that has not mounted
 *     yet, or whose restore has not landed, leaves what was remembered alone.
 *     Back, Forward, Back therefore lands on the list again, and a reader who
 *     scrolled on after the first Back lands there the second time. A Back or
 *     Forward only REFRESHES an entry that already exists; it never adds one
 *     (the menu drawer pops a marker entry on every close, and those are dead
 *     keys).
 *   - A REPLACE records nothing. It is the same page under a new key (the
 *     class page's tab taps, /search rewriting ?q=), so the old key can never be
 *     returned to and would only use up the memory: 53 tab taps used to push
 *     the list's own entry out. A pending restore and the page's live offset
 *     follow the new key.
 *   - A navigation to another pathname cancels a restore still waiting. A page
 *     rewriting its own query string (a REPLACE on the same pathname) does not.
 *   - The memory is the last 50 history entries, keyed by react-router's
 *     location.key. Nothing is written to storage.
 *
 * Everything touching the browser goes through ScrollEnv, so node --test can
 * drive the whole thing with a fake clock, a fake document height, fake scroll
 * events and fake reader input (tests/scroll-restore.test.mjs). iOS Safari has
 * no scroll anchoring, so the drift this repairs is a Chrome-family behaviour;
 * the restore itself is harmless there.
 */

import type { RouteNavigationType } from './routeScroll';

export const MAX_SAVED_POSITIONS = 50;
export const RESTORE_GIVE_UP_MS = 15000;

/** Insert-or-refresh, newest last, dropping the oldest past `max`. */
export function rememberScrollPosition(
  positions: Map<string, number>,
  key: string,
  y: number,
  max: number = MAX_SAVED_POSITIONS,
): void {
  if (!key || !Number.isFinite(y) || y < 0) return;
  positions.delete(key);
  positions.set(key, Math.round(y));
  while (positions.size > max) {
    const oldest = positions.keys().next().value;
    if (oldest === undefined) break;
    positions.delete(oldest);
  }
}

export interface RestoreDecisionInput {
  /** The pathname the previous page settled on, or null for the first page of the visit. */
  fromPathname: string | null;
  toPathname: string;
  navigationType: RouteNavigationType;
  /** The offset remembered for the history entry being returned to. */
  savedY: number | undefined;
}

/** True when a Back or Forward to a different page has an offset worth putting back. */
export function shouldRestoreScrollOnPop(input: RestoreDecisionInput): boolean {
  if (input.navigationType !== 'POP') return false;
  if (input.fromPathname === null || input.fromPathname === input.toPathname) return false;
  return typeof input.savedY === 'number' && input.savedY > 0;
}

/** The keys that move a page on their own. Anything else the reader types is not scrolling. */
export const SCROLL_KEYS: ReadonlySet<string> = new Set([
  'ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' ', 'Spacebar',
]);

/** Which way a scroll key would move the page: 1 down, -1 up, null for a key that does not scroll. */
export function keyDirection(key: string, shift: boolean): 1 | -1 | null {
  if (key === 'ArrowDown' || key === 'PageDown' || key === 'End') return 1;
  if (key === 'ArrowUp' || key === 'PageUp' || key === 'Home') return -1;
  if (key === ' ' || key === 'Spacebar') return shift ? -1 : 1;
  return null;
}

/** How many frames a wheel turn or key gets to show that it moved the window. */
export const MOVE_CHECK_FRAMES = 2;

export interface ReaderTrackerDeps {
  scrollY(): number;
  requestFrame(callback: () => void): number;
  /** Calls `handler` after the window scrolls (dispatched at the next rendering opportunity, after the input event that caused it). */
  onScroll(handler: () => void): void;
}

/**
 * Tells a reader who takes over the page from input that merely arrives. A wheel
 * turn or a scroll key is the reader's only if the window then moves the way it
 * pointed; a touch drag only if the window moves at all; a press on the
 * scrollbar is always the reader's. Everything else (a tap, a downward wheel
 * turn on a page too short to scroll) is noise to the restore.
 *
 * "Moved" is measured from the last offset a SCROLL EVENT reported, not from
 * the window's offset when the input handler runs: on a busy main thread (a
 * slow phone loading a list) the compositor has already scrolled the window by
 * the time the wheel handler runs, so reading the offset there sees a delta of
 * zero and throws a real scroll away (measured at 1280, CPU x8: the window
 * was already at 84 when the handler saw a wheel turn that had taken it from
 * 284).
 */
export function createReaderTracker(deps: ReaderTrackerDeps) {
  let moved = 0;
  let pending = 0;
  let lastSeenY = deps.scrollY();
  deps.onScroll(() => { lastSeenY = deps.scrollY(); });

  function watch(direction: -1 | 0 | 1): void {
    const before = lastSeenY;
    pending += 1;
    let frames = 0;
    const look = (): void => {
      const delta = deps.scrollY() - before;
      if (direction === 0 ? delta !== 0 : delta * direction > 0) {
        moved += 1;
        pending -= 1;
        return;
      }
      frames += 1;
      if (frames >= MOVE_CHECK_FRAMES) { pending -= 1; return; }
      deps.requestFrame(look);
    };
    deps.requestFrame(look);
  }

  return {
    wheel(deltaY: number): void { if (deltaY !== 0) watch(deltaY > 0 ? 1 : -1); },
    key(key: string, shift = false): void {
      const direction = keyDirection(key, shift);
      if (direction !== null) watch(direction);
    },
    touchMove(): void { watch(0); },
    scrollbarPress(): void { moved += 1; },
    /** Input that really moved the window, so far. */
    count: (): number => moved,
    /** True while a wheel turn, key or drag is still being judged. */
    pending: (): boolean => pending > 0,
  };
}

export interface ScrollEnv {
  now(): number;
  requestFrame(callback: () => void): number;
  cancelFrame(handle: number): void;
  scrollY(): number;
  /** The largest offset the document can scroll to right now. */
  maxScroll(): number;
  scrollTo(y: number): void;
  /** Counts the reader input that really MOVED the page (see createReaderTracker). */
  readerInputCount(): number;
  /** True while a wheel turn, scroll key or drag has been seen but not yet judged. */
  readerIntentPending(): boolean;
  /** Calls `handler` after the window scrolls (dispatched at the next rendering opportunity, never synchronously). */
  onScroll(handler: () => void): void;
}

export interface LocationChange {
  key: string;
  pathname: string;
  navigationType: RouteNavigationType;
}

export interface PopArrival {
  key: string;
  fromPathname: string | null;
  toPathname: string;
  navigationType: RouteNavigationType;
}

interface ActiveRestore {
  key: string;
  pathname: string;
  handle: number | null;
  stopped: boolean;
}

/** The page on screen for one history entry, once it has mounted. */
interface LivePage {
  key: string;
  /** The last offset a scroll event (or the mount) reported for this page. */
  y: number;
}

export function createScrollMemory(env: ScrollEnv) {
  const positions = new Map<string, number>();
  let shownKey: string | null = null;
  let shownPathname: string | null = null;
  let live: LivePage | null = null;
  let active: ActiveRestore | null = null;

  // Follow the page the reader is on. Not while its own restore is waiting:
  // the window is not where the reader left it, and must not be remembered.
  env.onScroll(() => {
    const page = live;
    if (page === null) return;
    if (active !== null && active.key === page.key) return;
    page.y = env.scrollY();
  });

  function cancelRestore(): void {
    if (!active) return;
    active.stopped = true;
    if (active.handle !== null) env.cancelFrame(active.handle);
    active = null;
  }

  function finish(run: ActiveRestore): void {
    run.stopped = true;
    if (run.handle !== null) env.cancelFrame(run.handle);
    if (active === run) {
      active = null;
      // The restore is over (landed, given up, or yielded to the reader): the
      // window is the reader's again, so the page is live from here.
      if (shownKey === run.key) live = { key: run.key, y: env.scrollY() };
    }
  }

  /**
   * Call once per location change, from a layout effect in a component placed
   * BEFORE the routed pages (so it runs ahead of their own layout effects in the
   * same commit). At that moment the page being left is still on screen, so a
   * forward navigation can read its true offset.
   */
  function noteLocationChange(change: LocationChange): void {
    if (shownKey !== null && shownKey !== change.key) {
      const leaving = shownKey;
      const sameEntry = change.navigationType === 'REPLACE' && change.pathname === shownPathname;
      if (sameEntry) {
        // The same page under a new key: its old key is dead. Carry the pending
        // restore and the live offset across, record nothing.
        if (active !== null && active.key === leaving) active.key = change.key;
        if (live !== null && live.key === leaving) live = { key: change.key, y: live.y };
      } else {
        const page = live !== null && live.key === leaving ? live : null;
        if (page !== null && change.navigationType === 'POP') {
          // Back or Forward out of a page: only REFRESH an entry that already
          // exists (a list left forward earlier, scrolled on since). A page
          // reached and left by Back never had one, and a drawer's marker entry
          // popped on every close must not add a dead key each time.
          if (positions.has(leaving)) rememberScrollPosition(positions, leaving, page.y);
        } else if (page !== null && change.navigationType === 'PUSH') {
          rememberScrollPosition(positions, leaving, env.scrollY());
        }
        // A page that never mounted, or whose restore had not landed, leaves
        // whatever was remembered for it alone.
        if (live !== null && live.key !== change.key) live = null;
      }
    }
    shownKey = change.key;
    shownPathname = change.pathname;
    if (active !== null && active.pathname !== change.pathname) cancelRestore();
  }

  /**
   * Call when the page for a location has just mounted. Returns true when a
   * restore was started.
   */
  function restoreOnPop(arrival: PopArrival): boolean {
    const y = positions.get(arrival.key);
    if (!shouldRestoreScrollOnPop({
      fromPathname: arrival.fromPathname,
      toPathname: arrival.toPathname,
      navigationType: arrival.navigationType,
      savedY: y,
    }) || y === undefined) {
      return false;
    }
    cancelRestore();
    // Input is counted from NOW. For the 220ms the old page fades out, a tap or
    // a key landed on the page being left.
    const baseline = env.readerInputCount();
    const run: ActiveRestore = { key: arrival.key, pathname: arrival.toPathname, handle: null, stopped: false };
    active = run;
    live = null;
    const startedAt = env.now();
    const tick = (): void => {
      run.handle = null;
      if (run.stopped) return;
      if (env.readerInputCount() !== baseline) { finish(run); return; }
      // A wheel turn or key was just seen: wait a frame to learn whether it
      // moves the page before scrolling over it.
      if (!env.readerIntentPending() && env.maxScroll() >= y - 1) {
        if (Math.abs(env.scrollY() - y) > 1) env.scrollTo(y);
        finish(run);
        return;
      }
      if (env.now() - startedAt >= RESTORE_GIVE_UP_MS) { finish(run); return; }
      run.handle = env.requestFrame(tick);
    };
    tick();
    return true;
  }

  /**
   * Call last in the page's mount effect, after any reset: the page is on
   * screen and, unless its own restore is still waiting, its offset is true.
   */
  function pageMounted(key: string): void {
    if (shownKey !== key) return;
    if (active !== null && active.key === key) return;
    live = { key, y: env.scrollY() };
  }

  return {
    noteLocationChange,
    restoreOnPop,
    pageMounted,
    cancelRestore,
    /** Test and debugging read: the offset remembered for an entry. */
    savedFor: (key: string): number | undefined => positions.get(key),
    savedCount: (): number => positions.size,
    restoring: (): boolean => active !== null,
  };
}

export type ScrollMemory = ReturnType<typeof createScrollMemory>;

function createBrowserEnv(): ScrollEnv {
  const reader = createReaderTracker({
    scrollY: () => window.scrollY,
    requestFrame: (callback) => window.requestAnimationFrame(callback),
    onScroll: (handler) => { window.addEventListener('scroll', handler, { passive: true }); },
  });
  window.addEventListener('wheel', (event: WheelEvent) => reader.wheel(event.deltaY), { passive: true });
  // A drag, not a tap: touchstart alone moves nothing.
  window.addEventListener('touchmove', () => reader.touchMove(), { passive: true });
  window.addEventListener('keydown', (event: KeyboardEvent) => reader.key(event.key, event.shiftKey));
  // A press on the scrollbar lands outside the document's client box.
  window.addEventListener('pointerdown', (event: PointerEvent) => {
    const root = document.documentElement;
    if (event.clientX >= root.clientWidth || event.clientY >= root.clientHeight) reader.scrollbarPress();
  }, { passive: true });
  return {
    now: () => performance.now(),
    requestFrame: (callback) => window.requestAnimationFrame(callback),
    cancelFrame: (handle) => window.cancelAnimationFrame(handle),
    scrollY: () => window.scrollY,
    maxScroll: () => Math.max(0, document.documentElement.scrollHeight - window.innerHeight),
    scrollTo: (y) => window.scrollTo({ top: y, left: 0, behavior: 'auto' }),
    readerInputCount: () => reader.count(),
    readerIntentPending: () => reader.pending(),
    onScroll: (handler) => { window.addEventListener('scroll', handler, { passive: true }); },
  };
}

let browserMemory: ScrollMemory | null = null;

/** The app's one scroll memory. Null outside a browser (node tests, SSR). */
export function getScrollMemory(): ScrollMemory | null {
  if (typeof window === 'undefined' || typeof document === 'undefined') return null;
  if (!browserMemory) browserMemory = createScrollMemory(createBrowserEnv());
  return browserMemory;
}
